import React, { useState, useMemo, useCallback, useEffect } from "react";
import { useInboxService } from "../../contexts/InboxServiceContext";
import { useEntriesService } from "./context/entries.context";
import { useTablePagination } from "../../../../hooks";
import ToastService from "../../../../services/toast.service";
import CreateWinzEntryModal from "./createWinzEntryModal";
import ChildLinkModal from "../childLinkModal";
import ExpandedRowActions from "./ExpandedRowActions";
import WinzEntriesTable from "./WinzEntriesTable";
import PdfModal from "./PdfModal";
import DeleteEntryModal from "./DeleteEntryModal";
import { formatInTimeZone } from "date-fns-tz";
import { Pagination, IconButton } from "rsuite";
import { useEditableRow, Input } from "../../../../components";
import { getStatusMessagesFromArrayString } from "../../../../utils/constants/status.helpers";
import { FaSave, FaTimes, FaEdit, FaDollarSign  } from "react-icons/fa";
import { Eventbus } from "../../../../utils";
import logger from "../../../../utils/logger";
import { useAuthenticationService } from "../../../../services/authentication.service";
import { events, roles } from "../../../../utils/constants";
import "../expanded.css";

export default function ExpandedRowContent({ rowData, rowKey }) {
  const timeZone = "Pacific/Auckland";
  const InboxService = useInboxService();
  const { displayData, setDisplayData, loading, setLoading, service: EntriesService } = useEntriesService();
  const rowId = rowData?.id;
  
  // Local state to track current email record data
  const [currentRowData, setCurrentRowData] = useState(rowData);
  const [isAdmin, setIsAdmin] = useState(useAuthenticationService().isInAnyRole(roles.ADMINS));
  const [searchTerm, setSearchTerm] = useState("");
  const [showPdfModal, setShowPdfModal] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [childLinkModalOpen, setChildLinkModalOpen] = useState(false);
  const [entryToUpdate, setEntryToUpdate] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [grandTotalEditing, setGrandTotalEditing] = useState(false);
  const [grandTotalValue, setGrandTotalValue] = useState(currentRowData.grandTotal || 0);
  // Sorting state
  const [sortColumn, setSortColumn] = useState("statusCodes");
  const [sortType, setSortType] = useState("desc");
  
  // Pagination
  const pagination = useTablePagination({
    initialPageSize: 5,
    updateQueryParams: () => {},
    getQueryParams: () => ({}),
  });

  // Setup event listeners for this row and load initial data
  useEffect(() => {
    const cleanup = EntriesService.setupEventListeners(rowId);

    // Load initial data for this row
    EntriesService.loadEntriesForRow(rowId, InboxService);

    return cleanup;
  }, [rowId, EntriesService, InboxService]);

  // Listen for SignalR email record updates to keep grandTotal in sync
  useEffect(() => {
    const handleEmailRecordUpdate = (updatedEmailRecords) => {
      // Handle both single records and arrays of records
      const recordsArray = Array.isArray(updatedEmailRecords) ? updatedEmailRecords : [updatedEmailRecords];
      
      // Find if our current row was updated
      const updatedRecord = recordsArray.find(record => record.id === rowId);
      if (updatedRecord) {
        setCurrentRowData(prev => ({
          ...prev,
          ...updatedRecord,
          exported: updatedRecord.batchId !== undefined && updatedRecord.batchId !== null
        }));
        
        // Update grandTotalValue if not currently editing
        if (!grandTotalEditing) {
          setGrandTotalValue(updatedRecord.grandTotal || 0);
        }
      }
    };

    // Listen for SignalR email record updates
    Eventbus.on(events.SIGNALR.WINZ_EMAIL_DATA_CHANGES, handleEmailRecordUpdate);

    return () => {
      Eventbus.off(handleEmailRecordUpdate);
    };
  }, [rowId, grandTotalEditing]);

  // Update local state when rowData prop changes
  useEffect(() => {
    setCurrentRowData(rowData);
    if (!grandTotalEditing) {
      setGrandTotalValue(rowData.grandTotal || 0);
    }
  }, [rowData, grandTotalEditing]);

  // Helper function to check if a row has an error
  const hasError = useCallback((rowData) => {
    const statusMsg = getStatusMessagesFromArrayString(rowData.errorCodes);
    return statusMsg && statusMsg.trim() !== "" && statusMsg !== "0" && !statusMsg.toLowerCase().includes("success");
  }, []);

  // Sort data function
  const sortData = useCallback(
    (data, column, type) => {
      if (!data || data.length === 0) return [];

      return [...data].sort((a, b) => {
        let x, y;

        switch (column) {
          case "hasError":
          case "statusCodes":
            x = hasError(a) ? 1 : 0;
            y = hasError(b) ? 1 : 0;
            break;
          case "currentWeek":
          case "ccahours":
            x = parseFloat(a[column]) || 0;
            y = parseFloat(b[column]) || 0;
            break;
          case "paymentDate":
          case "periodTo":
          case "renewalDate":
            x = new Date(a[column]);
            y = new Date(b[column]);
            break;
          default:
            x = a[column] || "";
            y = b[column] || "";
            if (typeof x === "string") x = x.toLowerCase();
            if (typeof y === "string") y = y.toLowerCase();
        }

        if (type === "asc") {
          return x > y ? 1 : x < y ? -1 : 0;
        } else {
          return x < y ? 1 : x > y ? -1 : 0;
        }
      });
    },
    [hasError]
  );

  // Handle sort
  const handleSortColumn = useCallback((sortColumn, sortType) => {
    setSortColumn(sortColumn);
    setSortType(sortType);
  }, []);

  // Handlers
  const handleSaveEntry = useCallback(
    async (id, updatedData) => {
      if (!id || !updatedData) throw new Error("Invalid data for saving entry");
      const existingData = displayData.find((entry) => entry.id === id);
      if (!existingData) throw new Error("Entry not found for saving");
      await InboxService.updateWinzEntry(id, updatedData);
      const updatedEntry = { ...existingData, ...updatedData };
      const updatedDisplayData = displayData.map((entry) => (entry.id === id ? updatedEntry : entry));
      EntriesService.updateDisplayData(updatedDisplayData);
      ToastService.success("Entry updated successfully");
    },
    [displayData, rowId, InboxService, EntriesService]
  );

  // Editable row
  const { editingRowId, editingData, saving, startEditing, cancelEditing, handleFieldChange, saveRow, isEditing } = useEditableRow({
    onSave: handleSaveEntry,
    onError: (error) => {
      logger.error("Error saving entry:", error);
      ToastService.error(error.response?.data || "Unknown error");
    },
  });

  // Grand total edit/save logic
  const handleEditGrandTotal = () => {
    setGrandTotalEditing(true);
    setGrandTotalValue(currentRowData.grandTotal || 0);
  };

  const handleGrandTotalChange = (e) => {
    setGrandTotalValue(e.target.value);
  };

  const handleSaveGrandTotal = async () => {
    try {
      const payload = {
        id: currentRowData.id,
        grandTotal: parseFloat(grandTotalValue)
      };
      await InboxService.updateGrandTotal(payload);
      ToastService.success("Grand total updated successfully");
      setGrandTotalEditing(false);
      // Optionally update local rowData.grandTotal if needed
    } catch (error) {
      ToastService.error("Failed to update grand total");
    }
  };

  const handleCancelGrandTotal = () => {
    setGrandTotalEditing(false);
    setGrandTotalValue(currentRowData.grandTotal || 0);
  };

  // Derived data
  const filteredData = useMemo(
    () =>
      searchTerm
        ? displayData.filter((entry) =>
            Object.values(entry).some(
              (val) =>
                val &&
                val
                  .toString()
                  .toLowerCase()
                  .includes((searchTerm || "").toLowerCase())
            )
          )
        : displayData,
    [searchTerm, displayData]
  );

  const entriesTotal = useMemo(() => {
    if (!displayData || displayData.length === 0) return 0;

    return displayData.reduce((acc, entry) => acc + (parseFloat(entry.currentWeek) || 0), 0);
  }, [displayData]);

  // Sort the filtered data
  const sortedData = useMemo(() => {
    return sortData(filteredData, sortColumn, sortType);
  }, [filteredData, sortColumn, sortType, sortData]);

  const { paginatedData, totalItems } = pagination.applyPagination(sortedData);

  // Reset to first page when search term changes
  useEffect(() => {
    if (searchTerm !== "") {
      pagination.resetToFirstPage();
    }
  }, [searchTerm]);

  const handleDeleteEntry = useCallback((entry) => {
    setEntryToUpdate(entry);
    setDeleteModalOpen(true);
  }, []);

  const handleLinkChild = useCallback((entry) => {
    setEntryToUpdate(entry);
    setChildLinkModalOpen(true);
  }, []);

  const confirmDelete = useCallback(async () => {
    if (!entryToUpdate) return;
    setDeleting(true);
    try {
      await InboxService.deleteWinzEntry(entryToUpdate.id);
      ToastService.success("Entry deleted successfully");
      setDeleteModalOpen(false);
      setEntryToUpdate(null);
      const updatedDisplayData = displayData.filter((entry) => entry.id !== entryToUpdate.id);
      EntriesService.updateDisplayData(updatedDisplayData);
    } catch (error) {
      ToastService.error(`Failed to delete entry: ${error.message || "Unknown error"}`);
    } finally {
      setDeleting(false);
    }
  }, [entryToUpdate, displayData, rowId, InboxService, EntriesService]);

  const cancelDelete = useCallback(() => {
    setDeleteModalOpen(false);
    setEntryToUpdate(null);
  }, []);

  const loadPdf = useCallback(() => {
    InboxService.getEmailPdf(rowId).then((response) => {
      const blobUrl = URL.createObjectURL(response);
      setPdfUrl(blobUrl);
    });
  }, [InboxService, rowId]);

  const reprocess = useCallback(() => {
    setLoading(true);
    InboxService.reprocess(rowId)
      .then((response) => {
        if (response && response.code === "ERR_CANCELED") {
          setLoading(false);
          return;
        }
        if (response && response.length > 0) {
          EntriesService.updateDisplayData(response);
          ToastService.success("Reprocessed successfully");
        } else {
          ToastService.error("No data to reprocess");
        }
        setLoading(false);
      })
      .catch((err) => {
        setLoading(false);
        ToastService.error("Error reprocessing data: " + (err.message || "Unknown error"));
      });
  }, [InboxService, rowId, EntriesService, setLoading]);

  return (
    <>
      <div style={{ padding: 16, borderRadius: 4, height: 600 }}>
        <div className="expanded-header-text">
          <div style={{ width: '100%'}}>
            <h5 style={{ margin: 0, marginBottom: 8 }}>Details for {currentRowData.emailSubject || currentRowData[rowKey]}</h5>
            Processed On: {currentRowData.dateProcessed ? formatInTimeZone(new Date(currentRowData.dateProcessed), timeZone, "dd/MM/yyyy HH:mm:ss") : ""}
            <br />
            {/* Flex container for grand totals and edit/input controls */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', minHeight: 55 }}>
              <div className={`grand-total ${entriesTotal?.toFixed(2) !== currentRowData?.grandTotal?.toFixed(2) ? "not-matching" : "matching"}`} style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                <span>PDF Grand Total: {currentRowData.grandTotal > 0 ? "$" + currentRowData.grandTotal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : "0.00"}</span>
                <span>Row Grand Total: ${entriesTotal?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
              </div>
              {/* Edit button or input/save/cancel controls, vertically centered and always next to the two lines */}
              {entriesTotal?.toFixed(2) !== currentRowData?.grandTotal?.toFixed(2) && (
                <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'center', alignItems: 'center', height: '100%', marginLeft: '30px' }}>
                  {grandTotalEditing ? (
                    <>
                      <Input
                        icon={<FaDollarSign />}
                        type="number"
                        value={grandTotalValue}
                        onChange={handleGrandTotalChange}
                        style={{ width: 150, marginBottom: 4 }}
                        data-testid="grand-total-input"
                      />
                      <div style={{ display: 'flex', flexDirection: 'row', gap: '4px' }}>
                        <IconButton
                          icon={<FaSave />}
                          className="edit-save-button"
                          size="xs"
                          appearance="subtle"
                          onClick={handleSaveGrandTotal}
                          title="Save"
                        />
                        <IconButton
                          icon={<FaTimes />}
                          className="edit-cancel-button"
                          size="xs"
                          appearance="subtle"
                          onClick={handleCancelGrandTotal}
                          title="Cancel"
                        />
                      </div>
                    </>
                  ) : (
                    <IconButton className="edit-button" icon={<FaEdit />} size="xs" appearance="subtle" onClick={handleEditGrandTotal} title="Edit" style={{ display: !isAdmin ? 'none' : 'block' }} />
                  )}
                </div>
              )}
            </div>
            <br />
          </div>
          <ExpandedRowActions
            displayData={displayData}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            onCreate={() => setShowCreateModal(true)}
            onReprocess={reprocess}
            onShowPdf={() => setShowPdfModal(true)}
            pdfAvailable={currentRowData && currentRowData.pdfUrl}
          />
        </div>
        <div style={{ marginTop: 16, height: 400 }}>
          {loading ? (
            <p>Loading details...</p>
          ) : filteredData.length > 0 ? (
            <>
              <WinzEntriesTable
                paginatedData={paginatedData}
                editingRowId={editingRowId}
                editingData={editingData}
                isEditing={isEditing}
                startEditing={startEditing}
                saveRow={saveRow}
                cancelEditing={cancelEditing}
                saving={saving}
                handleFieldChange={handleFieldChange}
                handleLinkChild={handleLinkChild}
                handleDeleteEntry={handleDeleteEntry}
                sortColumn={sortColumn}
                sortType={sortType}
                onSortColumn={handleSortColumn}
                emailData={rowData}
              />
              <Pagination
                prev
                next
                first
                last
                ellipsis
                boundaryLinks
                maxButtons={5}
                size="sm"
                layout={["total", "-", "limit", "|", "pager"]}
                total={totalItems}
                limitOptions={[5, 10, 20]}
                limit={pagination.pageSize}
                activePage={pagination.currentPage}
                onChangePage={pagination.handlePageChange}
                onChangeLimit={pagination.handlePageSizeChange}
                style={{ marginTop: 16 }}
              />
            </>
          ) : (
            <p data-testid="no-details">No additional details available.</p>
          )}
        </div>
      </div>
      <PdfModal open={showPdfModal} onOpen={loadPdf} onClose={() => setShowPdfModal(false)} pdfUrl={pdfUrl} rowData={rowData} timeZone={timeZone} />
      <CreateWinzEntryModal open={showCreateModal} onClose={() => setShowCreateModal(false)} service={InboxService} emailRecordId={rowId} />
      <ChildLinkModal open={childLinkModalOpen} onClose={() => setChildLinkModalOpen(false)} emailData={rowData} entryData={entryToUpdate} />
      <DeleteEntryModal open={deleteModalOpen} onClose={cancelDelete} onDelete={confirmDelete} deleting={deleting} entryToUpdate={entryToUpdate} />
    </>
  );
}
